import { contactSupport, reportBug, setupBillingCustomer } from '@/api/cloudFunctionWrappers'
import { apiSaveBundle } from '@/api/config'
import { apiSetupOnboardingResources, apiUpdatePayment } from '@/api/functions'
import {
  Billing,
  BugReport,
  BundlePeriod,
  Consents,
  ContactSupport,
  StorageTierCodename,
  UserProxy,
  UserTaxType,
} from '@/api/types/user'
import { Codename } from '@/api/types/workstation'
import {
  apiCancelSubscription,
  apiContinueCanceledSubscription,
  apiGetProfile,
  apiUpdateProfile,
} from '@/api/user'
import { addToWaitingList } from '@/api/waiting_list'
import router from '@/router'
import {
  createUserWithEmailAndPassword,
  ensureCurrentUser,
  getCurrentUserInfo,
  googleLogIn,
  reauthenticate,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from '@/services/auth'
import { getBillingPeriodObject } from '@/services/date'
import {
  trackBillingUpdateAttempt,
  trackBugReportAttempt,
  trackCompleteProfile,
  trackCotactSupportAttempt,
  trackEditProfileAttempt,
  trackEditProfileFailure,
  trackEditProfileSuccess,
  trackGoogleLoginAttempt,
  trackGoogleLoginFailure,
  trackGoogleLoginSuccess,
  trackGoogleRegistrationFailure,
  trackLoginAttempt,
  trackLoginFailure,
  trackLoginSuccess,
  trackLogout,
  trackPasswordResetAttempt,
  trackPasswordResetFailure,
  trackPasswordResetSuccess,
  trackPaymentMethodUpdateAttempt,
  trackRegistrationAttempt,
  trackRegistrationFailure,
  trackStoragePlanCancelAttempt,
  trackStoragePlanUpdateAttempt,
} from '@/services/tracking'
import { getTaxRateString } from '@/services/user'
import { initRequestHelper, isAlreadyPending } from '@/store/root'
import { WorkstationType } from '@/store/types/config'
import { RootState } from '@/store/types/root'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import {
  BillingUpdateInfo,
  EditableProfileInfo,
  PaymentMethodUpdateInfo,
  UserState,
} from '../types/user'

const getDefaultState = (): UserState => {
  return {
    profile: null,
  }
}

const moduleState = getDefaultState()

const moduleGetters: GetterTree<UserState, RootState> = {
  // to change - we have it from auth
  loggedIn(state) {
    return Boolean(state.profile)
  },
  displayName(state, getters) {
    if (!state.profile) {
      return 'Logged Out User'
    }
    const user = getters.user as UserProxy
    const fullName = [state.profile.first_name, state.profile.last_name].join(' ').trim()
    return fullName || user.email
  },
  profile(state) {
    return state.profile || {}
  },
  billing(state) {
    // return state.profile?.billing || {}
    return state.profile ? state.profile.billing || {} : {}
  },
  userFirstName(_state, getters) {
    const user = getters.user as UserProxy
    return user.profile.first_name || user.email || 'Logged Out User'
  },
  isRenderroAdmin(_state, getters) {
    return Boolean(getters.profile.is_renderro_admin)
  },
  hasTeamsEnabled(_state, getters) {
    return true
    // return Boolean(getCurrentUserInfo().email.endsWith('@renderro.com'))
  },
  workstationNotifyIfIdling(_state, getters) {
    return Boolean(getters.profile.workstation_notify_if_idling)
  },
  workstationTurnOffIdling(_state, getters) {
    return Boolean(getters.profile.workstation_turn_off_idling)
  },
  workstationTurnOffOnDisconnect(_state, getters) {
    return Boolean(getters.profile.workstation_turn_off_on_disconnect)
  },
  loginRequest(_state, _getters, rootState) {
    return rootState.requests.login
  },
  logOutRequest(_state, _getters, rootState) {
    return rootState.requests.logOut
  },
  socialRequest(_state, _getters, rootState) {
    return rootState.requests.social
  },
  registrationRequest(_state, _getters, rootState) {
    return rootState.requests.registration
  },
  profileUpdateRequest(_state, _getters, rootState) {
    return rootState.requests.profileUpdate
  },
  billingUpdateRequest(_state, _getters, rootState) {
    return rootState.requests.billingUpdate
  },
  paymentMethodUpdateRequest(_state, _getters, rootState) {
    return rootState.requests.paymentMethodUpdate
  },
  createResourcesRequest(_state, _getters, rootState) {
    return rootState.requests.createResources
  },
  setupRequest(_state, _getters, rootState) {
    return rootState.requests.setupOnboarding
  },
  passwordChangeRequest(_state, _getters, rootState) {
    return rootState.requests.passwordChange
  },
  passwordResetRequest(_state, _getters, rootState) {
    return rootState.requests.passwordReset
  },
  storageTierUpdateRequest(_state, _getters, rootState) {
    return rootState.requests.setupOnboarding
  },
  notificationsAboutTeamsFeatureToggleRequest(_state, _getters, rootState) {
    return rootState.requests.notificationsAboutTeamsFeatureToggle
  },
  bugReportRequest(_state, _getters, rootState) {
    return rootState.requests.bugReport
  },
  contactSupportRequest(_state, _getters, rootState) {
    return rootState.requests.contactSupport
  },
  subscriptionCancelRequest(_state, _getters, rootState) {
    return rootState.requests.subscriptionCancel
  },
  canceledSubscriptionContinueRequest(_state, _getters, rootState) {
    return rootState.requests.canceledSubscriptionContinue
  },
  creditDataUpdateRequest(_state, _getters, rootState) {
    return rootState.requests.creditDataUpdate
  },
  hasGivenConsent(_state, getters) {
    if (getters.profile.consents) {
      const { terms_of_service, terms_and_conditions, privacy_policy, data_processing } =
        getters.profile.consents
      return terms_of_service && terms_and_conditions && privacy_policy && data_processing
    }
    return false
  },
  user(_state, getters, _rootState, rootGetters) {
    const currentUser = getCurrentUserInfo()
    const workstations = rootGetters['workstation/workstations'] || []
    const drives = rootGetters['drive/drives'] || []

    return new UserProxy(
      currentUser.userId,
      getters.profile,
      currentUser.email,
      currentUser.displayName,
      currentUser.photoURL,
      currentUser.provider,
      workstations,
      drives,
    )
  },
  getTaxRate(_state, getters) {
    const taxType: UserTaxType = getters.user.taxType
    return getTaxRateString(taxType)
  },
  shouldGetNotified(_state, getters) {
    return getters.profile?.should_get_notified_about_teams_feature
  },
  selectedTier(_state, getters) {
    return getters.profile?.selected_tier
  },
  storagePlan(_state, getters) {
    return getters.profile?.storage_plan
  },
  photoURL(_state, getters) {
    return getters.user.photoURL
  },
  // The same as billingInformationIsCorrect from services/user
  hasPaymentMethod(_state, getters): boolean {
    return (
      !!getters.billing?.stripe_active_payment_method_identifier &&
      !!getters.billing?.stripe_customer_identifier
    )
  },
  ownsAnyResources(_state, _getters, _rootState, rootGetters): boolean {
    return rootGetters['workstation/ownsAny'] || rootGetters['drive/ownsAny']
  },
  workstationsLimit(_state, getters): number {
    return getters.profile?.limits?.max_number_of_workstations || 0
  },
  userRegion(_state, getters) {
    return getters.profile?.region
  },
  userStripeCustomerId(_state, getters) {
    return getters.profile?.billing?.stripe_customer_identifier
  },
  trialEndSecondsTimestamp(_state, getters) {
    return getters.storagePlan?.trial_end
  },
  userStatus(_state, getters) {
    return getters.profile?.status
  },
  subscriptionToBeCancelledAt(_state, getters) {
    return getters.storagePlan?.terminate_all_resources_ms
  },
  upcomingTier(_state, getters) {
    return getters.selectedTier?.upcoming_tier
  },
  billingPeriodObject(_state, getters) {
    if (getters.storagePlan?.created) {
      const { created, trial_end } = getters.storagePlan
      const bundlePeriod = getters.selectedTier?.bundlePeriod
      return getBillingPeriodObject(created, trial_end, bundlePeriod)
    }
    return null
  },
  isUserOnTrial(_state, getters) {
    return getters.billingPeriodObject?.isTrialOngoing
  },
}

export const moduleActions: ActionTree<UserState, RootState> = {
  async signUp({ dispatch, commit, getters }, { email, password, firstName, lastName }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'registration')
    try {
      trackRegistrationAttempt(email)
      const user = await createUserWithEmailAndPassword(email, password)
      const consents: Consents = {
        terms_and_conditions: true,
        terms_of_service: true,
        privacy_policy: true,
        data_processing: true,
      }
      await dispatch('updateProfile', {
        first_name: firstName,
        last_name: lastName,
        consents,
      })
      setSuccess('Account created successfully!')
      trackCompleteProfile(getters.profile, consents)

      if (user && !user.emailVerified) {
        await user.sendEmailVerification({
          url: `${document.location.origin}/success`,
        })
        router.replace('verify-email')
      } else {
        router.replace('home')
      }
    } catch (err: any) {
      setFailure(err.message)
      trackRegistrationFailure(email)
      throw err
    }
  },

  async signUpToWaitingList({ commit }, { email }) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'registration')
    try {
      const response = await addToWaitingList({ email: email })
      setSuccess(response.result)
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  /**
   * Happens at /complete-profile, updates profile with GDPR and name data
   * @param context
   * @param profileInfo
   */
  async socialSignUp(
    { commit, dispatch, getters },
    { firstName, lastName }: { firstName: string; lastName: string },
  ) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'registration')
    try {
      const consents: Consents = {
        terms_and_conditions: true,
        terms_of_service: true,
        privacy_policy: true,
        data_processing: true,
      }
      await dispatch('updateProfile', {
        first_name: firstName,
        last_name: lastName,
        consents,
      })
      setSuccess('Account created successfully!')
      trackCompleteProfile(getters.profile, consents)
      router.replace('/dashboard')
    } catch (err: any) {
      setFailure(err.message)
      trackGoogleRegistrationFailure(getters.user.email)
      throw err
    }
  },

  async login({ dispatch, commit, getters }, { email, password }) {
    if (isAlreadyPending(getters.loginRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'login')
    try {
      trackLoginAttempt(email)
      await signInWithEmailAndPassword(email, password)
      await dispatch('ensureProfile')
      trackLoginSuccess(email)
      router.replace('/dashboard')
      setSuccess('Logged in!')
    } catch (err: any) {
      trackLoginFailure(email)
      setFailure(err.message)
      throw err
    }
  },

  /**
   *  Happens upon clicking Google btn on /login or /sign-up pages,
   *  just logs in with Google.
   * @param param0 context.dispatch, context.commit
   */
  async socialLogin({ dispatch, commit, getters, rootGetters }) {
    if (isAlreadyPending(getters.socialRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'social')
    try {
      trackGoogleLoginAttempt()
      await googleLogIn(rootGetters['config/allowsSignUp'])
      await dispatch('ensureProfile')
      trackGoogleLoginSuccess(getters.user.email)
      router.replace('/dashboard')
      setSuccess('Logged in!')
    } catch (err: any) {
      trackGoogleLoginFailure(getters.user.email)
      setFailure(err.message)
      throw err
    }
  },

  async logOut({ dispatch, commit, getters }) {
    if (isAlreadyPending(getters.logOutRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'logOut')
    try {
      await dispatch('signOut')
      trackLogout(getters.user.email)
      router.replace('/login')
      setSuccess('Logged out!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  /**
   * Used in mounted in views where we want to ensure user is logged out, i.e. /verify-email, /check-inbox...
   * @param context
   */
  async logOutWithoutRedirect({ dispatch, commit, getters }) {
    if (isAlreadyPending(getters.logOutRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'logOut')
    try {
      await dispatch('signOut')
      trackLogout(getters.user.email)
      setSuccess('Logged out!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  /**
   * Used for auth logout. Resets store.
   * @param context
   */
  async signOut({ dispatch }) {
    return Promise.all([signOut(), dispatch('resetGlobalState', null, { root: true })])
  },

  /*
   * Ensure the vuex profile data is filled,
   * either from firestore (retrieved user),
   * Google and/or by default, empty details.
   */
  async ensureProfile({ commit, dispatch }) {
    // happens often, seems good time to update workstation usage data
    dispatch('workstation/setUsageData', null, { root: true })

    try {
      ensureCurrentUser()
      const profile = await apiGetProfile()
      commit('SET_PROFILE', profile)
      dispatch('workstation/bindUserWorkstations', null, { root: true })
      dispatch('drive/bindUserDrives', null, { root: true })
      dispatch('config/getConfig', null, { root: true })
    } catch (err: any) {
      // Logged out
      // causes redirection loop? verify
      await dispatch('logOutWithoutRedirect')
    }
  },

  /**
   * Used to update / sync vuex and firebase data
   * @param param0 _context.commit
   * @param profile profile data to be updated
   */
  async updateProfile({ commit, getters }, editedProfile: EditableProfileInfo) {
    if (isAlreadyPending(getters.profileUpdateRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'profileUpdate')
    const withoutUndefined: Partial<EditableProfileInfo> = { ...editedProfile }
    const keys = Object.keys(editedProfile) as Array<keyof EditableProfileInfo>
    keys.forEach((key) => (withoutUndefined[key] === undefined ? delete withoutUndefined[key] : {}))
    try {
      trackEditProfileAttempt(getters.profile, Object.keys(withoutUndefined))
      await apiUpdateProfile(withoutUndefined)
      commit('UPDATE_PROFILE', withoutUndefined)
      trackEditProfileSuccess(getters.profile, Object.keys(withoutUndefined))
      setSuccess('Profile updated!')
    } catch (err: any) {
      trackEditProfileFailure(getters.profile, Object.keys(withoutUndefined))

      setFailure(err.message)
      throw err
    }
  },

  async changePassword({ commit, getters }, { currentPassword, newPassword, newPasswordRepeat }) {
    if (isAlreadyPending(getters.passwordChangeRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'passwordChange')
    if (newPassword !== newPasswordRepeat) {
      setFailure('New passwords do not match')
      return
    }
    try {
      await reauthenticate(currentPassword)
      const user = ensureCurrentUser()
      await user.updatePassword(newPassword)
      setSuccess('Password updated!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async passwordReset({ commit, getters }, { email }) {
    if (isAlreadyPending(getters.passwordResetRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'passwordReset')
    try {
      trackPasswordResetAttempt(email)
      await sendPasswordResetEmail(email)
      trackPasswordResetSuccess(email)
      setSuccess('Email sent!')
      router.replace('check-your-inbox')
    } catch (err: any) {
      trackPasswordResetFailure(email)
      setFailure(err.message)
      throw err
    }
  },

  async updatePaymentMethod(
    { commit, dispatch, getters },
    { stripe, cardholderName, card }: PaymentMethodUpdateInfo,
  ) {
    if (isAlreadyPending(getters.paymentMethodUpdateRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'paymentMethodUpdate')
    try {
      trackPaymentMethodUpdateAttempt(getters.profile)
      const stripeObject = { stripe, cardholderName, card }
      const email = getters.user.email
      await apiUpdatePayment(stripeObject, getters.billing, email)
      // Refresh profile after recent changes in billing
      await dispatch('ensureProfile')
      setSuccess('Payment method set!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async updateBilling(
    { commit, dispatch, getters },
    {
      billing: { fullName, addressLine1, addressLine2, city, postalCode, country },
    }: BillingUpdateInfo,
  ) {
    if (isAlreadyPending(getters.billingUpdateRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'billingUpdate')
    try {
      trackBillingUpdateAttempt(getters.profile, country)
      const { stripe_customer_identifier, stripe_active_payment_method_identifier, card } =
        getters.billing
      let extraFields = {}
      if (card) {
        extraFields = {
          last_4_digits: card.last_4_digits,
          cardholder_name: card.cardholder_name,
          exp_month: card.exp_month,
          exp_year: card.exp_year,
        }
      }
      await setupBillingCustomer({
        billing: {
          full_name: fullName,
          address_line_1: addressLine1,
          address_line_2: addressLine2,
          postal_code: postalCode,
          city,
          country,
          stripe_customer_identifier: stripe_customer_identifier,
          stripe_active_payment_method_identifier: stripe_active_payment_method_identifier,
          card: {
            ...card,
            ...extraFields,
          },
        },
      })
      // Refresh profile after recent changes in billing
      await dispatch('ensureProfile')
      setSuccess('Billing info updated!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async reportBug({ commit, getters }, report: BugReport) {
    if (isAlreadyPending(getters.bugReportRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'bugReport')
    trackBugReportAttempt(getters.profile, report.category, report.description)
    const successMessage =
      report.category != 'CALL REQUEST'
        ? 'Bug reported!'
        : 'Thanks for reaching out. Our representative will be in touch ASAP'
    try {
      await reportBug(report)
      router.go(-1)
      setSuccess(successMessage)
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async contactSupport({ commit, getters }, report: ContactSupport) {
    if (isAlreadyPending(getters.contactSupportRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'contactSupport')
    trackCotactSupportAttempt(getters.profile, report.category, report.description)
    const successMessage = 'Our representative will be in touch ASAP'
    try {
      await contactSupport(report)
      setSuccess(successMessage)
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async cancelSubscription({ commit, dispatch, getters }, reason: string) {
    if (isAlreadyPending(getters.subscriptionCancelRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'subscriptionCancel')
    try {
      trackStoragePlanCancelAttempt(getters.profile, getters.selectedTier)
      await apiCancelSubscription(reason)
      dispatch('ensureProfile')
      setSuccess('Subscription cancelled!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async continueCanceledSubscription({ commit, dispatch, getters }) {
    if (isAlreadyPending(getters.continueCanceledSubscriptionRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'canceledSubscriptionContinue')
    // track sth?
    try {
      await apiContinueCanceledSubscription()
      dispatch('ensureProfile')
      setSuccess(`Subscription will continue, renews on ${getters.billingPeriodObject?.end}`)
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  /**
   * Update selected plan, and (optionally):
   * - update payment details (if paymentMethod object is present)
   * - create resource pair (if there is no drive present)
   *
   * @param context context
   * @param options including new plan codename, optional payment method, period and whether trial is offered
   */
  async setup(
    { commit, dispatch, getters, rootGetters },
    {
      codename,
      paymentMethod,
      bundlePeriod,
      allowTrial,
      affiliateHash,
    }: {
      codename: StorageTierCodename
      paymentMethod?: PaymentMethodUpdateInfo
      bundlePeriod: BundlePeriod
      allowTrial: boolean
      affiliateHash?: string
    },
  ) {
    if (isAlreadyPending(getters.setupRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'setupOnboarding')
    try {
      trackStoragePlanUpdateAttempt(getters.profile, codename)

      if (!getters.hasPaymentMethod && !paymentMethod) {
        throw new Error('Attempted to setup with no payment method present.')
      }

      // payment, if needed
      if (paymentMethod) {
        const email = getters.user.email
        const billing: Billing = { ...getters.billing, affiliate_hash: affiliateHash }
        await apiUpdatePayment(paymentMethod, billing, email)
      }

      const workstationTypes = rootGetters['config/workstationTypes']

      if (!workstationTypes) {
        await dispatch('config/getConfig', null, { root: true })
      }

      const workstationCodename = workstationTypes.filter((wt: WorkstationType) => wt.selectable)[0]
        .codename as Codename

      // save storage plan
      await apiSaveBundle({ codename, bundlePeriod, allowTrial })

      // setup resources, if needed
      const drives = rootGetters['drive/numberOfOwnerDrives']
      const workstations = rootGetters['workstation/numberOfOwnerWorkstations']
      if (!drives) {
        const driveOnly = !!workstations // if user owns workstations, let's only create a drive
        await apiSetupOnboardingResources({ driveOnly, workstationCodename })
      }
      await dispatch('ensureProfile')
      await dispatch('payment/refreshCredits')

      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async resetUserState({ commit }) {
    commit('resetState')
  },
}

const moduleMutations: MutationTree<UserState> = {
  SET_PROFILE(state, profile) {
    state.profile = profile
  },
  UPDATE_PROFILE(state, changes) {
    state.profile = { ...state.profile, ...changes }
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

const user: Module<UserState, RootState> = {
  namespaced: true,
  state: moduleState,
  getters: moduleGetters,
  mutations: moduleMutations,
  actions: moduleActions,
}
export default user
